// src/pages/MemberLogin.js
// ✅ FINAL READY-TO-PASTE (FULL INTEGRATED - NO PLACEHOLDER LINES)
// ✅ High-tech Admin UI: Sidebar + Topbar + Dashboard + Requests + Docs + Benefits
// ✅ Language: EN / HI / GU switch INSIDE this page (word-to-word)
// ✅ Requests: realtime + category filter + BLACK BORDER table + Delete (pending only) + Locked if generated/confirmed
// ✅ Auto-restore session so request submit ke baad login page nahi aayega
// ✅ No external i18n files required (page dictionary used)

import { useEffect, useMemo, useState } from "react";
import { db, auth, storage } from "../firebaseConfig";

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { generateApplicationForm } from "../pdfs/generateApplicationForm";
import generateMembershipCard from "../pdfs/generateMembershipCard";
import generateShareCertificate from "../pdfs/generateShareCertificate";
import generateWelcomeLetter from "../pdfs/generateWelcomeLetter";
import generateIDCard from "../pdfs/GenerateIDCard";

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/* =========================
   Helpers
========================= */
const cx = (...xs) => xs.filter(Boolean).join(" ");

// ✅ Prevent "Timeout (u)" crash
const withTimeout = (p, ms = 12000) =>
  Promise.race([
    p,
    new Promise((_, rej) =>
      setTimeout(() => rej(new Error("Request timeout. Please retry.")), ms)
    ),
  ]);

/* =========================
   Existing constants (unchanged)
========================= */
const COMPONENT_TITLES = {
  PLANTING: "Planting Material",
  MACH_BANK: "Machinery Bank / Tractor",
  IRRIGATION: "Drip / Sprinkler / Farm Pond",
  INM_IPM: "Bio Fertilizer / INM / IPM",
  GAP_SOIL: "Soil Testing / GAP Advisory",
  PRECISION: "Precision Tech (Drone / IoT)",
  INNOVATIVE: "Innovative Tools",
};

const MASTER_COMPONENTS = [
  {
    id: "PLANTING",
    icon: "🌱",
    label: "Planting Material",
    desc: "Certified planting material support – orchard/seedlings/quality plants (as per scheme norms).",
    link: "/farmer/planting-material",
    ctaLabel: "Open Planting",
    live: false,
  },
  {
    id: "MACH_BANK",
    icon: "🚜",
    label: "Machinery Bank / Tractor",
    desc: "Tractor, implements, and custom-hiring support (purchase or rent) – subsidy guidance as per norms.",
    link: "/farmer/machinery-bank",
    ctaLabel: "Open Machinery",
    live: true,
  },
  {
    id: "IRRIGATION",
    icon: "💧",
    label: "Drip / Sprinkler / Farm Pond",
    desc: "Micro-irrigation (drip/sprinkler), pipeline, and farm-pond guidance to save water & increase output.",
    link: "/farmer/irrigation",
    ctaLabel: "Open Irrigation",
    live: false,
  },
  {
    id: "INM_IPM",
    icon: "🧪",
    label: "Bio Fertilizer / INM / IPM",
    desc: "Bio fertilizers, INM/IPM kits and crop-wise support for better yield & pest control (as per scheme norms).",
    link: "/farmer/inm-ipm",
    ctaLabel: "Open Bio Inputs",
    live: false,
  },
  {
    id: "GAP_SOIL",
    icon: "🧬",
    label: "Soil Testing / GAP Advisory",
    desc: "Soil & water testing + GAP advisory for scientific farming and better subsidy compliance.",
    link: "/farmer/soil-gap",
    ctaLabel: "Open Soil/GAP",
    live: false,
  },
  {
    id: "PRECISION",
    icon: "📡",
    label: "Precision Tech (Drone / IoT)",
    desc: "Drone services, sensors, and smart farming support where applicable under scheme guidelines.",
    link: "/farmer/precision-tech",
    ctaLabel: "Explore Precision",
    live: false,
  },
  {
    id: "INNOVATIVE",
    icon: "🛠️",
    label: "Innovative Tools",
    desc: "Labour-saving tools & equipment support relevant for agriculture operations (as per scheme norms).",
    link: "/farmer/innovative-tools",
    ctaLabel: "Explore Tools",
    live: false,
  },
];

/* =========================
   Local page dictionary (EN/HI/GU) — word-to-word
========================= */
const DICT = {
  en: {
    brand: "Shine Agri • Member Admin",
    member: "Member",
    id: "ID",
    logout: "Logout",
    quotations: "Quotations",
    confirmed: "Confirmed",

    nav: "Navigation",
    memberPanel: "Member Panel",
    tipSidebar: "Tip: Requests tab shows Quotation PDF",
    dashboard: "Dashboard",
    requests: "My Requests",
    documents: "Documents",
    benefits: "Benefits",

    totalRequests: "Total Requests",
    quotationsPdf: "Quotations (PDF)",
    pendingQuotation: "Pending Quotation",
    docsUploaded: "Docs Uploaded",
    chartTitle: "Quick Chart",
    chartSub: "Requests vs Quotations vs Confirmed",

    openMachinery: "Open Machinery Bank",
    viewRequests: "View My Requests",
    back: "Back",

    docsTitle: "Upload / Update Documents",
    uploaded: "Uploaded",
    actionUploadSelected: "Upload Selected",
    uploading: "Uploading…",
    uploadSuccess: "✅ Documents uploaded successfully. They are saved in your account.",
    uploadFailed: "❌ Upload failed:",
    folders: "Document Folders",
    foldersSub: "Open only when you want to upload/replace documents",

    current: "Current",
    view: "View",
    notUploaded: "Not Uploaded",
    pending: "Pending",
    uploadedOk: "Uploaded",
    waiting: "Waiting…",
    generated: "Generated",
    notGenerated: "Not Generated",
    submitted: "Submitted",
    confirmedOk: "Confirmed",

    reqTitle: "My Requests & Quotations",
    reqSub: "Vendor quotation generate/confirm will update automatically.",
    applyNew: "Apply New Request",
    noRequests: "No requests yet. Apply from Machinery Bank.",

    machine: "Machine",
    vendor: "Vendor",
    status: "Status",
    quotation: "Quotation",
    action: "Action",
    viewDownload: "View / Download",

    category: "Category",
    allCategories: "All Categories",
    delete: "Delete",
    locked: "Locked",
    deleteConfirm: "Delete this request? It will also be removed from vendor dashboard.",
    deleteBlocked: "❌ Cannot delete: quotation generated or request confirmed.",
    deleteOk: "✅ Request deleted successfully.",

    benefitsTitle: "Available Farmer Benefits",
    benefitsSub: "Short list shown (expand if needed).",
    showAll: "Show All",
    showLess: "Show Less",
    enabled: "Enabled",
    live: "Live",
    soon: "Soon",
    applySoon: "Apply (Soon)",

    authTitle: "Secure Login",
    authSub: "Login with Mobile + Password.",
    login: "Login",
    forgot: "Forgot Password",
    mobileNumber: "Mobile Number",
    password: "Password",
    forgotQ: "Forgot password?",
    resetTitle: "Reset Password",
    resetSub: "Enter mobile → reset link will be sent to registered Gmail.",
    sendReset: "Send Reset Link",
    sending: "Sending...",
    backToLogin: "Back to Login",
    checking: "Checking...",
  },

  hi: {
    brand: "शाइन एग्री • मेंबर एडमिन",
    member: "सदस्य",
    id: "आईडी",
    logout: "लॉगआउट",
    quotations: "कोटेशन",
    confirmed: "कन्फर्म",

    nav: "नेविगेशन",
    memberPanel: "मेंबर पैनल",
    tipSidebar: "टिप: Requests टैब में Quotation PDF दिखेगा",
    dashboard: "डैशबोर्ड",
    requests: "मेरे रिक्वेस्ट",
    documents: "दस्तावेज़",
    benefits: "लाभ",

    totalRequests: "कुल रिक्वेस्ट",
    quotationsPdf: "कोटेशन (PDF)",
    pendingQuotation: "पेंडिंग कोटेशन",
    docsUploaded: "दस्तावेज़ अपलोड",
    chartTitle: "क्विक चार्ट",
    chartSub: "रिक्वेस्ट बनाम कोटेशन बनाम कन्फर्म",

    openMachinery: "मशीनरी बैंक खोलें",
    viewRequests: "मेरे रिक्वेस्ट देखें",
    back: "वापस",

    docsTitle: "दस्तावेज़ अपलोड / अपडेट",
    uploaded: "अपलोड",
    actionUploadSelected: "चुने हुए अपलोड",
    uploading: "अपलोड हो रहा है…",
    uploadSuccess: "✅ दस्तावेज़ सफलतापूर्वक अपलोड हो गए।",
    uploadFailed: "❌ अपलोड फेल:",
    folders: "डॉक्यूमेंट फ़ोल्डर",
    foldersSub: "सिर्फ जरूरत हो तब खोलें",

    current: "करेंट",
    view: "देखें",
    notUploaded: "अपलोड नहीं",
    pending: "पेंडिंग",
    uploadedOk: "अपलोडेड",
    waiting: "वेटिंग…",
    generated: "जनरेट",
    notGenerated: "नहीं जनरेट",
    submitted: "सबमिट",
    confirmedOk: "कन्फर्म",

    reqTitle: "मेरे रिक्वेस्ट और कोटेशन",
    reqSub: "वेंडर कोटेशन जनरेट/कन्फर्म होने पर ऑटो अपडेट होगा।",
    applyNew: "नई रिक्वेस्ट करें",
    noRequests: "अभी कोई रिक्वेस्ट नहीं। मशीनरी बैंक से आवेदन करें।",

    machine: "मशीन",
    vendor: "वेंडर",
    status: "स्थिति",
    quotation: "कोटेशन",
    action: "एक्शन",
    viewDownload: "देखें / डाउनलोड",

    category: "कैटेगरी",
    allCategories: "सभी कैटेगरी",
    delete: "डिलीट",
    locked: "लॉक्ड",
    deleteConfirm: "इस रिक्वेस्ट को डिलीट करें? वेंडर डैशबोर्ड से भी हट जाएगी।",
    deleteBlocked: "❌ डिलीट नहीं हो सकता: कोटेशन जनरेट या कन्फर्म हो चुका है।",
    deleteOk: "✅ रिक्वेस्ट डिलीट हो गई।",

    benefitsTitle: "उपलब्ध किसान लाभ",
    benefitsSub: "कम लिस्ट दिखेगी (चाहें तो बढ़ाएं)।",
    showAll: "सब दिखाएं",
    showLess: "कम दिखाएं",
    enabled: "सक्रिय",
    live: "लाइव",
    soon: "जल्द",
    applySoon: "अभी नहीं",

    authTitle: "सिक्योर लॉगिन",
    authSub: "मोबाइल + पासवर्ड से लॉगिन करें।",
    login: "लॉगिन",
    forgot: "पासवर्ड भूल गए",
    mobileNumber: "मोबाइल नंबर",
    password: "पासवर्ड",
    forgotQ: "पासवर्ड भूल गए?",
    resetTitle: "पासवर्ड रीसेट",
    resetSub: "मोबाइल डालें → Gmail पर रीसेट लिंक आएगा।",
    sendReset: "रीसेट लिंक भेजें",
    sending: "भेज रहे हैं…",
    backToLogin: "लॉगिन पर जाएं",
    checking: "चेक हो रहा है…",
  },

  gu: {
    brand: "શાઇન એગ્રી • મેમ્બર એડમિન",
    member: "સભ્ય",
    id: "આઈડી",
    logout: "લોગઆઉટ",
    quotations: "ક્વોટેશન",
    confirmed: "કન્ફર્મ",

    nav: "નેવિગેશન",
    memberPanel: "મેમ્બર પેનલ",
    tipSidebar: "ટિપ: Requests ટૅબમાં Quotation PDF દેખાશે",
    dashboard: "ડૅશબોર્ડ",
    requests: "મારી રિક્વેસ્ટ",
    documents: "દસ્તાવેજો",
    benefits: "લાભ",

    totalRequests: "કુલ રિક્વેસ્ટ",
    quotationsPdf: "ક્વોટેશન (PDF)",
    pendingQuotation: "પેન્ડિંગ ક્વોટેશન",
    docsUploaded: "દસ્તાવેજ અપલોડ",
    chartTitle: "ક્વિક ચાર્ટ",
    chartSub: "રિક્વેસ્ટ vs ક્વોટેશન vs કન્ફર્મ",

    openMachinery: "મશીનરી બેંક ખોલો",
    viewRequests: "મારી રિક્વેસ્ટ જુઓ",
    back: "પાછા",

    docsTitle: "દસ્તાવેજ અપલોડ / અપડેટ",
    uploaded: "અપલોડ",
    actionUploadSelected: "પસંદ કરેલ અપલોડ",
    uploading: "અપલોડ થઈ રહ્યું છે…",
    uploadSuccess: "✅ દસ્તાવેજ સફળતાપૂર્વક અપલોડ થયા.",
    uploadFailed: "❌ અપલોડ ફેલ:",
    folders: "ડોક્યુમેન્ટ ફોલ્ડર",
    foldersSub: "જરૂર હોય ત્યારે જ ખોલો",

    current: "કરંટ",
    view: "જુઓ",
    notUploaded: "અપલોડ નથી",
    pending: "પેન્ડિંગ",
    uploadedOk: "અપલોડેડ",
    waiting: "વેઇટિંગ…",
    generated: "જનરેટ",
    notGenerated: "જનરેટ નથી",
    submitted: "સબમિટ",
    confirmedOk: "કન્ફર્મ",

    reqTitle: "મારી રિક્વેસ્ટ અને ક્વોટેશન",
    reqSub: "વેન્ડર ક્વોટેશન જનરેટ/કન્ફર્મ થાય એટલે અહીં ઓટો અપડેટ થશે.",
    applyNew: "નવી રિક્વેસ્ટ કરો",
    noRequests: "હજુ કોઈ રિક્વેસ્ટ નથી. મશીનરી બેંકમાંથી અરજી કરો.",

    machine: "મશીન",
    vendor: "વેન્ડર",
    status: "સ્થિતિ",
    quotation: "ક્વોટેશન",
    action: "એકશન",
    viewDownload: "જુઓ / ડાઉનલોડ",

    category: "કેટેગરી",
    allCategories: "બધી કેટેગરી",
    delete: "ડિલીટ",
    locked: "લોક્ડ",
    deleteConfirm: "આ રિક્વેસ્ટ ડિલીટ કરવી છે? વેન્ડર ડૅશબોર્ડ માંથી પણ હટી જશે.",
    deleteBlocked: "❌ ડિલીટ નથી થઈ શકે: ક્વોટેશન જનરેટ અથવા કન્ફર્મ થઈ ગઈ છે.",
    deleteOk: "✅ રિક્વેસ્ટ ડિલીટ થઈ ગઈ.",

    benefitsTitle: "ઉપલબ્ધ ખેડૂત લાભ",
    benefitsSub: "ઓછી યાદી દેખાશે (જરૂર હોય તો વધારશો).",
    showAll: "બધું બતાવો",
    showLess: "ઓછું બતાવો",
    enabled: "એનેબલ",
    live: "લાઇવ",
    soon: "જલ્દી",
    applySoon: "હજુ નહીં",

    authTitle: "સિક્યોર લોગિન",
    authSub: "મોબાઇલ + પાસવર્ડથી લોગિન કરો.",
    login: "લોગિન",
    forgot: "પાસવર્ડ ભૂલ્યા",
    mobileNumber: "મોબાઇલ નંબર",
    password: "પાસવર્ડ",
    forgotQ: "પાસવર્ડ ભૂલ્યા?",
    resetTitle: "પાસવર્ડ રીસેટ",
    resetSub: "મોબાઇલ દાખલ કરો → Gmail પર રીસેટ લિંક આવશે.",
    sendReset: "રીસેટ લિંક મોકલો",
    sending: "મોકલી રહ્યા છીએ…",
    backToLogin: "લોગિન પર જાઓ",
    checking: "ચેક થઈ રહ્યું છે…",
  },
};

/* =========================
   Doc UI
========================= */
function DocTile({ icon, title, hint, url, accept, onPick, langText }) {
  const uploaded = !!url;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-[18px]">
            {icon}
          </div>
          <div>
            <div className="text-[12px] font-extrabold text-slate-900">{title}</div>
            {hint ? (
              <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
            ) : null}
          </div>
        </div>
        <span
          className={cx(
            "inline-flex px-2 py-1 rounded-full border text-[10px] font-extrabold",
            uploaded
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-amber-50 text-amber-800 border-amber-200"
          )}
        >
          {uploaded ? `${langText.uploadedOk} ✅` : `${langText.pending} ⏳`}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <input
          type="file"
          accept={accept}
          onChange={(e) => onPick(e.target.files?.[0] || null)}
          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-[11px]"
        />

        <div className="flex items-center justify-between">
          <div className="text-[11px] font-extrabold text-slate-700">
            {langText.current}:
          </div>
          {uploaded ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] font-extrabold text-emerald-700 underline"
            >
              {langText.view} ✅
            </a>
          ) : (
            <span className="text-[11px] font-extrabold text-slate-500">
              {langText.notUploaded}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================
   MAIN
========================= */
export default function MemberLogin() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ in-page language switch
  const [lang, setLang] = useState(() => localStorage.getItem("member_lang") || "en");
  const L = DICT[lang] || DICT.en;

  useEffect(() => {
    localStorage.setItem("member_lang", lang);
    try {
      i18n?.changeLanguage?.(lang);
    } catch {}
    document.documentElement.lang = lang;
  }, [lang, i18n]);

  // Auth UI
  const [authTab, setAuthTab] = useState("login");
  const [loading, setLoading] = useState(false);

  // Login values
  const [id, setId] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [member, setMember] = useState(null);

  // Forgot password
  const [fpMobile, setFpMobile] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpMsg, setFpMsg] = useState("");

  // Admin layout nav
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activePanel, setActivePanel] = useState(() => {
    const p = new URLSearchParams(location.search).get("panel");
    return p || "dashboard";
  });

  // Components
  const [components, setComponents] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(true);
  const [showAllBenefits, setShowAllBenefits] = useState(false);

  // Docs upload
  const [docFiles, setDocFiles] = useState({
    photo: null,
    aadhaar: null,
    election: null,
    land: null,
    light: null,
    bank_cheque: null,
    bank_passbook: null,
  });
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [docsOpen, setDocsOpen] = useState(true);

  const setFile = (key, file) => setDocFiles((p) => ({ ...p, [key]: file || null }));

  // My Requests / Quotations (Realtime)
  const [myApps, setMyApps] = useState([]);
  const [myAppsLoading, setMyAppsLoading] = useState(false);

  // Category filter
  const [reqGroupFilter, setReqGroupFilter] = useState("ALL");

  // ✅ AUTO RESTORE SESSION
  useEffect(() => {
    const restore = async () => {
      try {
        const u = auth.currentUser;
        if (!u) return;
        if (member?.id) return;

        const docId = localStorage.getItem("member_doc_id");
        if (docId) {
          const snap = await getDoc(doc(db, "members", docId));
          if (snap.exists()) {
            const data = { id: snap.id, ...(snap.data() || {}) };
            setMember(data);
            return;
          }
        }

        const q1 = query(collection(db, "members"), where("auth_uid", "==", u.uid));
        const s1 = await getDocs(q1);
        if (!s1.empty) {
          const d = s1.docs[0];
          const data = { id: d.id, ...(d.data() || {}) };
          localStorage.setItem("member_doc_id", data.id);
          localStorage.setItem("member_id", String(data.member_id || ""));
          localStorage.setItem("member_mobile", String(data.mobile || ""));
          setMember(data);
        }
      } catch (e) {
        console.warn("restore session failed:", e?.message || e);
      }
    };

    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  /* =========================
     Upload helpers
  ========================= */
  const uploadSingleFile = async (file, pathPrefix) => {
    if (!file) return null;
    const safeName = String(file.name || "file").replace(/\s+/g, "_");
    const fileRef = ref(storage, `${pathPrefix}/${Date.now()}-${safeName}`);
    await withTimeout(uploadBytes(fileRef, file), 20000);
    return await withTimeout(getDownloadURL(fileRef), 20000);
  };

  const uploadDocuments = async () => {
    if (!member?.id) return alert("Member record not found. Please login again.");
    setUploadMsg("");
    setUploadingDocs(true);

    try {
      const basePath = `members_docs/${member.id}`;

      const [
        photoUrl,
        aadhaarUrl,
        electionUrl,
        landUrl,
        lightUrl,
        chequeUrl,
        passbookUrl,
      ] = await Promise.all([
        uploadSingleFile(docFiles.photo, `${basePath}/photo`),
        uploadSingleFile(docFiles.aadhaar, `${basePath}/aadhaar`),
        uploadSingleFile(docFiles.election, `${basePath}/election`),
        uploadSingleFile(docFiles.land, `${basePath}/land_record`),
        uploadSingleFile(docFiles.light, `${basePath}/light_bill`),
        uploadSingleFile(docFiles.bank_cheque, `${basePath}/bank_cheque`),
        uploadSingleFile(docFiles.bank_passbook, `${basePath}/bank_passbook`),
      ]);

      const updates = { documents_updatedAt: serverTimestamp() };
      if (photoUrl) updates["documents.photo_url"] = photoUrl;
      if (aadhaarUrl) updates["documents.aadhaar_url"] = aadhaarUrl;
      if (electionUrl) updates["documents.election_url"] = electionUrl;
      if (landUrl) updates["documents.land_record_url"] = landUrl;
      if (lightUrl) updates["documents.light_bill_url"] = lightUrl;
      if (chequeUrl) updates["documents.bank_cheque_url"] = chequeUrl;
      if (passbookUrl) updates["documents.bank_passbook_url"] = passbookUrl;

      await withTimeout(updateDoc(doc(db, "members", member.id), updates), 12000);

      setMember((prev) => ({
        ...(prev || {}),
        documents: {
          ...(prev?.documents || {}),
          ...(photoUrl ? { photo_url: photoUrl } : {}),
          ...(aadhaarUrl ? { aadhaar_url: aadhaarUrl } : {}),
          ...(electionUrl ? { election_url: electionUrl } : {}),
          ...(landUrl ? { land_record_url: landUrl } : {}),
          ...(lightUrl ? { light_bill_url: lightUrl } : {}),
          ...(chequeUrl ? { bank_cheque_url: chequeUrl } : {}),
          ...(passbookUrl ? { bank_passbook_url: passbookUrl } : {}),
        },
      }));

      setDocFiles({
        photo: null,
        aadhaar: null,
        election: null,
        land: null,
        light: null,
        bank_cheque: null,
        bank_passbook: null,
      });

      setUploadMsg(L.uploadSuccess);
    } catch (e) {
      console.error("uploadDocuments error:", e);
      const msg = `${L.uploadFailed} ${e?.message || e}`;
      setUploadMsg(msg);
      alert(msg);
    } finally {
      setUploadingDocs(false);
    }
  };

  const normalizeMemberIdInput = (x) => {
    const s = String(x || "").trim();
    if (!s) return "";
    if (s.startsWith("SAAM/NOMINAL/MEMBER/")) return s;

    const m = s.match(/^SAAM-(\d{4})-(\d{1,6})$/i);
    if (m) {
      const year = m[1];
      const seq = String(m[2]).padStart(4, "0");
      return `SAAM/${year}/${seq}`;
    }
    return s;
  };

  /* =========================
     Load benefit components
  ========================= */
  useEffect(() => {
    let alive = true;

    const loadComponents = async () => {
      setComponentsLoading(true);
      try {
        const snap = await withTimeout(getDocs(collection(db, "benefit_components")), 12000);
        if (!alive) return;

        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...(d.data() || {}) }));
        list.sort((a, b) => (a.name || a.id || "").localeCompare(b.name || b.id || ""));
        setComponents(list);
      } catch (err) {
        console.warn("benefit_components load failed:", err?.message || err);
        if (!alive) return;
        setComponents([]);
      } finally {
        if (!alive) return;
        setComponentsLoading(false);
      }
    };

    loadComponents();
    return () => {
      alive = false;
    };
  }, []);

  /* =========================
     My Requests listener
  ========================= */
  useEffect(() => {
    if (!member?.id && !member?.mobile) return;

    setMyAppsLoading(true);
    setMyApps([]);

    let unsub1 = null;
    let unsub2 = null;
    let fallbackStarted = false;

    const sortList = (list) => {
      list.sort((a, b) => {
        const aa = a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
        const bb = b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
        return bb - aa;
      });
      return list;
    };

    const startFallbackMobile = () => {
      if (fallbackStarted) return;
      fallbackStarted = true;

      const m = String(member?.mobile || "").trim();
      if (!/^\d{10}$/.test(m)) {
        setMyAppsLoading(false);
        return;
      }

      const q2 = query(collection(db, "machinery_applications"), where("farmerMobile", "==", m));
      unsub2 = onSnapshot(
        q2,
        (snap) => {
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, ...(d.data() || {}) }));
          setMyApps(sortList(list));
          setMyAppsLoading(false);
        },
        (err) => {
          console.warn("myApps mobile snapshot error:", err);
          setMyApps([]);
          setMyAppsLoading(false);
        }
      );
    };

    if (member?.id) {
      const q1 = query(collection(db, "machinery_applications"), where("memberDocId", "==", member.id));
      unsub1 = onSnapshot(
        q1,
        (snap) => {
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, ...(d.data() || {}) }));
          const sorted = sortList(list);

          if (sorted.length === 0) startFallbackMobile();
          else {
            setMyApps(sorted);
            setMyAppsLoading(false);
          }
        },
        (err) => {
          console.warn("myApps memberDocId snapshot error:", err);
          startFallbackMobile();
        }
      );
    } else {
      startFallbackMobile();
    }

    return () => {
      try { unsub1 && unsub1(); } catch {}
      try { unsub2 && unsub2(); } catch {}
    };
  }, [member?.id, member?.mobile]);

  /* =========================
     Category filter
  ========================= */
  const groupOptions = useMemo(() => {
    const s = new Set();
    myApps.forEach((a) => {
      const g = String(a.machGroup || "").trim();
      if (g) s.add(g);
    });
    return ["ALL", ...Array.from(s)];
  }, [myApps]);

  const myAppsFiltered = useMemo(() => {
    if (reqGroupFilter === "ALL") return myApps;
    return myApps.filter((a) => String(a.machGroup || "") === reqGroupFilter);
  }, [myApps, reqGroupFilter]);

  /* =========================
     Delete (pending only)
  ========================= */
  const canDeleteReq = (a) => {
    const st = String(a.status || "").toLowerCase();
    const generated = a.quotationGenerated === true || !!a.quotationPdfUrl;
    return st !== "processed" && !generated;
  };

  const handleDeleteReq = async (a) => {
    if (!a?.id) return;
    if (!canDeleteReq(a)) {
      alert(L.deleteBlocked);
      return;
    }
    const ok = window.confirm(L.deleteConfirm);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "machinery_applications", a.id));
      alert(L.deleteOk);
    } catch (e) {
      alert("❌ Delete failed: " + (e?.message || e));
    }
  };

  /* =========================
     Login / Forgot / Logout
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedMobile = String(mobile || "").trim();
    const normalizedId = normalizeMemberIdInput(id);

    if (!/^\d{10}$/.test(trimmedMobile)) {
      alert("Please enter valid 10-digit Mobile Number.");
      return;
    }
    if (!password) {
      alert("Please enter Password.");
      return;
    }

    try {
      setLoading(true);

      const q1 = query(collection(db, "members"), where("mobile", "==", trimmedMobile));
      const snap = await withTimeout(getDocs(q1), 12000);

      if (snap.empty) {
        setMember(null);
        alert("No membership found for this mobile number.");
        return;
      }

      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));
      const data = docs[0];

      if (normalizedId) {
        const storedId = String(data.member_id || "").trim();
        if (storedId && storedId !== normalizedId) {
          setMember(null);
          alert("Invalid Membership ID or Mobile Number.");
          return;
        }
      }

      const st = String(data.status || "").toLowerCase();
      if (st !== "approved") {
        setMember(null);
        alert("Your membership is still pending for approval.");
        return;
      }

      const paid = String(data.payment_status || "").toUpperCase();
      if (paid !== "PAID") {
        setMember(null);
        alert("Your membership fee payment is not completed. Please complete payment first.");
        return;
      }

      const email = String(data.email || "").trim();
      if (!email) {
        setMember(null);
        alert("Email not found in member record. Please contact support.");
        return;
      }

      await withTimeout(signInWithEmailAndPassword(auth, email, password), 12000);

      try {
        if (!data.auth_uid && auth.currentUser?.uid) {
          await withTimeout(
            updateDoc(doc(db, "members", data.id), {
              auth_uid: auth.currentUser.uid,
              last_loginAt: serverTimestamp(),
            }),
            12000
          );
        } else {
          await withTimeout(updateDoc(doc(db, "members", data.id), { last_loginAt: serverTimestamp() }), 12000);
        }
      } catch (e2) {
        console.warn("last_loginAt update skipped:", e2?.message || e2);
      }

      localStorage.setItem("member_doc_id", data.id);
      localStorage.setItem("member_id", String(data.member_id || ""));
      localStorage.setItem("member_mobile", String(data.mobile || ""));

      setMember(data);
      setActivePanel("dashboard");
    } catch (err) {
      console.error("Login error:", err);
      alert(err?.message || "Login failed. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const m = String(fpMobile || "").trim();
    if (!/^\d{10}$/.test(m)) {
      alert("Enter valid 10-digit mobile number.");
      return;
    }

    setFpLoading(true);
    setFpMsg("");
    try {
      const q1 = query(collection(db, "members"), where("mobile", "==", m));
      const snap = await withTimeout(getDocs(q1), 12000);

      if (snap.empty) {
        setFpMsg("❌ No membership found for this mobile.");
        return;
      }

      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));
      const data = docs[0];

      const email = String(data.email || "").trim();
      if (!email) {
        setFpMsg("❌ Email not found. Please contact support.");
        return;
      }

      await withTimeout(
  sendPasswordResetEmail(auth, email, {
    url: `${window.location.origin}/member-login`,
    handleCodeInApp: false,
  }),
  12000
);
      setFpMsg(`✅ Password reset link sent to: ${email}`);
    } catch (e) {
      console.error("Forgot password error:", e);
      setFpMsg("❌ " + (e?.message || "Failed to send reset email. Try again later."));
    } finally {
      setFpLoading(false);
    }
  };

  const handleLogout = async () => {
    setMember(null);
    setId("");
    setMobile("");
    setPassword("");

    localStorage.removeItem("member_doc_id");
    localStorage.removeItem("member_id");
    localStorage.removeItem("member_mobile");

    try { await withTimeout(signOut(auth), 8000); } catch {}
    try { navigate("/login", { replace: true }); } catch {}
  };

  /* =========================
     Derived
  ========================= */
  const masterCards = useMemo(() => {
    return MASTER_COMPONENTS.map((mc) => {
      const conf = components.find((c) => c.id === mc.id) || {};
      const active = conf.active === undefined ? true : conf.active !== false;

      const title = conf.name || COMPONENT_TITLES[mc.id] || mc.label || mc.id;
      const description =
        conf.description ||
        mc.desc ||
        "This category is enabled by the Society. Online application will open as per schedule.";

      return { ...mc, title, description, active };
    });
  }, [components]);

  const buildMachineryLink = () => {
    if (!member) return "/farmer/machinery-bank";
    const params = new URLSearchParams();
    params.set("memberId", member.member_id || "");
    if (member.state) params.set("state", member.state);
    if (member.district) params.set("district", member.district);
    if (member.taluka) params.set("taluka", member.taluka);
    return `/farmer/machinery-bank?${params.toString()}`;
  };

  const isNominalMember =
    String(member?.membership_type || "").trim().toUpperCase() === "NOMINAL" ||
    String(member?.member_id || "").trim().toUpperCase().startsWith("SAAM/NOMINAL/MEMBER/");

  const docsFilled = member?.documents || {};

  const benefitsToShow = useMemo(() => {
    const base = masterCards.filter((c) => c.active);
    return showAllBenefits ? base : base.slice(0, 3);
  }, [masterCards, showAllBenefits]);

  const docsCount = useMemo(() => {
    const urls = [
      docsFilled.photo_url,
      docsFilled.aadhaar_url,
      docsFilled.election_url,
      docsFilled.land_record_url,
      docsFilled.light_bill_url,
      docsFilled.bank_cheque_url,
      docsFilled.bank_passbook_url,
    ].filter(Boolean);
    return { uploaded: urls.length, total: 7 };
  }, [docsFilled]);

  const stats = useMemo(() => {
    const totalReq = myApps.length;
    const confirmed = myApps.filter((a) => String(a.status || "").toLowerCase() === "processed").length;
    const quotations = myApps.filter((a) => !!a.quotationPdfUrl).length;
    const pendingQ = Math.max(0, totalReq - quotations);
    return { totalReq, confirmed, quotations, pendingQ };
  }, [myApps]);

  /* =========================
     AUTH PAGE (before login)
  ========================= */
  if (!member) {
    return (
      <div className="min-h-screen bg-[radial-gradient(1200px_520px_at_50%_-120px,#dbeafe_0%,#f1f5f9_45%,#ffffff_100%)]">
        <div className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-6 py-6 bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest font-extrabold text-white/75">
                      {L.brand}
                    </div>
                    <div className="mt-2 text-[26px] font-extrabold leading-tight">
                      High-Tech Member Portal
                    </div>
                    <div className="mt-2 text-[13px] text-white/85 max-w-xl">
                      ✔️ Dashboard • ✔️ Tables • ✔️ Documents • ✔️ Quotation PDF • ✔️ Benefits
                    </div>
                  </div>

                  <div className="shrink-0 flex items-center gap-1">
                    {["en", "hi", "gu"].map((x) => (
                      <button
                        key={x}
                        type="button"
                        onClick={() => setLang(x)}
                        className={cx(
                          "px-3 py-1.5 rounded-full text-[12px] font-extrabold border",
                          lang === x
                            ? "bg-white text-slate-900 border-white"
                            : "bg-white/10 text-white border-white/20 hover:bg-white/15"
                        )}
                      >
                        {x.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[12px] font-extrabold text-slate-900">✔️ Charts</div>
                  <div className="text-[11px] text-slate-600 mt-1">Quick overview</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[12px] font-extrabold text-slate-900">✔️ Tables</div>
                  <div className="text-[11px] text-slate-600 mt-1">Requests tracking</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[12px] font-extrabold text-slate-900">✔️ Auth</div>
                  <div className="text-[11px] text-slate-600 mt-1">Mobile + Password</div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="text-[12px] font-extrabold text-slate-900">✔️ PDF</div>
                  <div className="text-[11px] text-slate-600 mt-1">Quotation download</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden">
              <div className="px-6 py-5 bg-gradient-to-r from-blue-950 via-slate-900 to-blue-950 text-white">
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-white/80">
                  {L.authTitle}
                </div>
                <div className="mt-1 text-[12px] text-white/90">{L.authSub}</div>
              </div>

              <div className="p-5">
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setAuthTab("login")}
                    className={cx(
                      "flex-1 px-4 py-2 rounded-2xl text-[12px] font-extrabold border",
                      authTab === "login"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    {L.login}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthTab("forgot")}
                    className={cx(
                      "flex-1 px-4 py-2 rounded-2xl text-[12px] font-extrabold border",
                      authTab === "forgot"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    {L.forgot}
                  </button>
                </div>

                <div className="hidden">
                  <input type="text" value={id} onChange={(e) => setId(e.target.value)} />
                </div>

                {authTab === "login" ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-[12px] font-extrabold mb-1 text-slate-800">
                        {L.mobileNumber} *
                      </label>
                      <input
                        type="text"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        required
                        inputMode="numeric"
                        maxLength={10}
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                        placeholder={L.mobileNumber}
                      />
                    </div>

                    <div>
                      <label className="block text-[12px] font-extrabold mb-1 text-slate-800">
                        {L.password} *
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full border border-slate-300 rounded-2xl px-4 py-3 text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setAuthTab("forgot")}
                        className="mt-2 text-[11px] font-extrabold text-blue-900 hover:underline"
                      >
                        {L.forgotQ}
                      </button>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className={cx(
                        "w-full px-6 py-3 rounded-2xl font-extrabold shadow text-white text-[12px]",
                        "bg-emerald-600 hover:bg-emerald-700 transition",
                        loading ? "opacity-60 cursor-not-allowed" : ""
                      )}
                    >
                      {loading ? L.checking : `🚀 ${L.login}`}
                    </button>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[12px] font-extrabold text-slate-900">🔁 {L.resetTitle}</div>
                      <div className="text-[11px] text-slate-600 mt-1">{L.resetSub}</div>

                      <div className="mt-3 flex flex-col sm:flex-row gap-2">
                        <input
                          value={fpMobile}
                          onChange={(e) => setFpMobile(e.target.value)}
                          placeholder={L.mobileNumber}
                          inputMode="numeric"
                          maxLength={10}
                          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-[11px] bg-white"
                        />
                        <button
                          type="button"
                          onClick={handleForgotPassword}
                          disabled={fpLoading}
                          className="px-4 py-2 rounded-xl text-[11px] font-extrabold text-white bg-blue-900 hover:bg-blue-950 disabled:opacity-60"
                        >
                          {fpLoading ? L.sending : L.sendReset}
                        </button>
                      </div>

                      {fpMsg && <div className="mt-2 text-[11px] font-semibold text-slate-700">{fpMsg}</div>}
                    </div>

                    <button
                      type="button"
                      onClick={() => setAuthTab("login")}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 text-[12px] font-extrabold text-slate-700 bg-white hover:bg-slate-50"
                    >
                      ⬅ {L.backToLogin}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 text-[11px] text-slate-500">
              Tip: Apply quotation → Vendor generates PDF → It will show in <b>{L.requests}</b> automatically.
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =========================
     AFTER LOGIN (Admin Layout)
  ========================= */
  const MENU = [
    { id: "dashboard", label: L.dashboard, icon: "📊" },
    { id: "requests", label: L.requests, icon: "🧾" },
    { id: "docs", label: L.documents, icon: "📁" },
    { id: "benefits", label: L.benefits, icon: "🎯" },
  ];

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Topbar */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[12px] font-extrabold"
          >
            ☰
          </button>

          <div className="flex-1">
            <div className="text-[12px] font-extrabold text-slate-900">{L.brand}</div>
            <div className="text-[11px] text-slate-600">
              {L.member}: <b>{member.name || "-"}</b> • {L.id}: <b>{member.member_id || "-"}</b>
            </div>
          </div>

          {/* Language switch INSIDE dashboard */}
          <div className="hidden md:flex items-center gap-1">
            {["en", "hi", "gu"].map((x) => (
              <button
                key={x}
                type="button"
                onClick={() => setLang(x)}
                className={cx(
                  "px-3 py-1.5 rounded-full text-[12px] font-extrabold border",
                  lang === x
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                )}
              >
                {x.toUpperCase()}
              </button>
            ))}
          </div>

          <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full border text-[11px] font-extrabold bg-indigo-50 text-indigo-700 border-indigo-200">
            {L.quotations}: {stats.quotations}
          </span>
          <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full border text-[11px] font-extrabold bg-emerald-50 text-emerald-700 border-emerald-200">
            {L.confirmed}: {stats.confirmed}
          </span>

          <button
            type="button"
            onClick={handleLogout}
            className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[12px] font-extrabold shadow"
          >
            🚪 {L.logout}
          </button>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 py-6 flex gap-4">
        {/* Sidebar */}
        <aside className={cx("shrink-0 transition-all", sidebarOpen ? "w-[260px]" : "w-[74px]")}>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white">
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-white/70">{L.nav}</div>
              <div className="mt-1 text-[13px] font-extrabold">
                {sidebarOpen ? L.memberPanel : "MP"}
              </div>
            </div>

            <div className="p-2">
              {MENU.map((m) => {
                const active = activePanel === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setActivePanel(m.id)}
                    className={cx(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left border mb-2",
                      active
                        ? "bg-indigo-50 border-indigo-200 text-indigo-900"
                        : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span className="text-[16px]">{m.icon}</span>
                    {sidebarOpen ? <span className="text-[12px] font-extrabold">{m.label}</span> : null}
                  </button>
                );
              })}

              <div className="px-3 py-2 text-[10px] text-slate-500">
                {sidebarOpen ? L.tipSidebar : "Tip"}
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 space-y-4">
          {/* DASHBOARD */}
          {activePanel === "dashboard" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-indigo-600 to-violet-600" />
                  <div className="p-4">
                    <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500">
                      {L.totalRequests}
                    </div>
                    <div className="mt-1 text-[22px] font-extrabold text-slate-900">{stats.totalReq}</div>
                    <div className="mt-1 text-[11px] text-slate-600">{L.requests}</div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-emerald-600 to-teal-600" />
                  <div className="p-4">
                    <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500">
                      {L.quotationsPdf}
                    </div>
                    <div className="mt-1 text-[22px] font-extrabold text-slate-900">{stats.quotations}</div>
                    <div className="mt-1 text-[11px] text-slate-600">PDF</div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-600" />
                  <div className="p-4">
                    <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500">
                      {L.pendingQuotation}
                    </div>
                    <div className="mt-1 text-[22px] font-extrabold text-slate-900">{stats.pendingQ}</div>
                    <div className="mt-1 text-[11px] text-slate-600">{L.waiting}</div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-rose-600 to-pink-600" />
                  <div className="p-4">
                    <div className="text-[11px] uppercase tracking-widest font-extrabold text-slate-500">
                      {L.docsUploaded}
                    </div>
                    <div className="mt-1 text-[22px] font-extrabold text-slate-900">
                      {docsCount.uploaded}/{docsCount.total}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-600">{L.documents}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="text-[12px] font-extrabold text-slate-900">{L.chartTitle}</div>
                <div className="text-[11px] text-slate-600 mt-1">{L.chartSub}</div>

                <div className="mt-4 space-y-3">
                  {[
                    { label: L.totalRequests, val: stats.totalReq, max: Math.max(1, stats.totalReq), tone: "bg-indigo-600" },
                    { label: L.quotationsPdf, val: stats.quotations, max: Math.max(1, stats.totalReq), tone: "bg-emerald-600" },
                    { label: L.confirmed, val: stats.confirmed, max: Math.max(1, stats.totalReq), tone: "bg-amber-500" },
                  ].map((x) => {
                    const pct = Math.min(100, Math.round((x.val / x.max) * 100));
                    return (
                      <div key={x.label}>
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-extrabold text-slate-800">{x.label}</span>
                          <span className="font-extrabold text-slate-700">{x.val}</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className={cx("h-2 rounded-full", x.tone)} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex gap-2 flex-wrap">
                  <a
                    href={buildMachineryLink()}
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-extrabold"
                  >
                    🚜 {L.openMachinery}
                  </a>
                  <button
                    type="button"
                    onClick={() => setActivePanel("requests")}
                    className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[12px] font-extrabold text-slate-800"
                  >
                    🧾 {L.viewRequests}
                  </button>
                </div>
              </div>

              {/* Official docs buttons */}
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="text-[12px] font-extrabold text-slate-900">Documents Download</div>
                <div className="text-[11px] text-slate-600 mt-1">Your official documents</div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                  <button
                    onClick={() => generateApplicationForm(member)}
                    className="rounded-2xl bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 shadow font-extrabold text-[11px]"
                  >
                    📄 Application PDF
                  </button>

                  {!isNominalMember && (
                    <button
                      onClick={() => generateMembershipCard(member)}
                      className="rounded-2xl bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 shadow font-extrabold text-[11px]"
                    >
                      🪪 Membership Card
                    </button>
                  )}

                  {!isNominalMember && (
                    <button
                      onClick={() => generateShareCertificate(member)}
                      className="rounded-2xl bg-pink-600 hover:bg-pink-700 text-white px-3 py-2 shadow font-extrabold text-[11px]"
                    >
                      📜 Share Certificate
                    </button>
                  )}

                  <button
                    onClick={() => generateWelcomeLetter(member)}
                    className="rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 shadow font-extrabold text-[11px]"
                  >
                    📬 Welcome Letter
                  </button>

                  {!isNominalMember && (
                    <button
                      onClick={() => generateIDCard(member)}
                      className="rounded-2xl bg-slate-900 hover:bg-slate-950 text-white px-3 py-2 shadow font-extrabold text-[11px]"
                    >
                      🪪 ID Card
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* REQUESTS (BLACK BORDER TABLE) */}
          {activePanel === "requests" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <div className="text-[12px] font-extrabold text-slate-900">{L.reqTitle}</div>
                    <div className="text-[11px] text-slate-600 mt-1">{L.reqSub}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="text-[11px] font-extrabold text-slate-700">{L.category}:</div>
                    <select
                      value={reqGroupFilter}
                      onChange={(e) => setReqGroupFilter(e.target.value)}
                      className="px-3 py-2 rounded-xl border border-black bg-white text-[12px] font-extrabold"
                    >
                      {groupOptions.map((g) => (
                        <option key={g} value={g}>
                          {g === "ALL" ? L.allCategories : g}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {myAppsLoading ? (
                  <div className="text-[12px] text-slate-600">{L.checking}</div>
                ) : myAppsFiltered.length === 0 ? (
                  <div className="text-[12px] text-slate-600">{L.noRequests}</div>
                ) : (
                  <div className="rounded-xl border-2 border-black overflow-auto max-h-[520px] bg-white">
                    <table className="min-w-full text-[11px] border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-slate-100">
                          {[ "#", L.machine, L.vendor, L.status, L.quotation, L.action ].map((h) => (
                            <th
                              key={h}
                              className="px-3 py-2 border border-black font-extrabold text-slate-800 text-left"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {myAppsFiltered.map((a, idx) => {
                          const st = String(a.status || "submitted").toLowerCase();
                          const qGen = !!a.quotationPdfUrl || a.quotationGenerated === true;

                          return (
                            <tr key={a.id} className="hover:bg-slate-50">
                              <td className="px-3 py-2 border border-black font-extrabold">{idx + 1}</td>

                              <td className="px-3 py-2 border border-black">
                                <div className="font-extrabold text-slate-900">
                                  {(a.companyBrand || a.brand || "-")} {(a.modelName || "")}{" "}
                                  {a.hpRating ? `(${a.hpRating})` : ""}
                                </div>
                                <div className="text-[10px] text-slate-600">
                                  Group: <b>{a.machGroup || "-"}</b>
                                </div>
                              </td>

                              <td className="px-3 py-2 border border-black">
                                <div className="font-extrabold text-slate-900">{a.vendorName || "-"}</div>
                                <div className="text-[10px] text-slate-600">
                                  {a.vendorTaluka ? `${a.vendorTaluka}, ` : ""}{a.vendorDistrict || ""}
                                </div>
                              </td>

                              <td className="px-3 py-2 border border-black">
                                {st === "processed" ? (
                                  <span className="inline-flex px-2 py-1 rounded-full border border-black text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                                    {L.confirmedOk} ✅
                                  </span>
                                ) : (
                                  <span className="inline-flex px-2 py-1 rounded-full border border-black text-[10px] font-extrabold bg-amber-100 text-amber-900">
                                    {L.submitted} ⏳
                                  </span>
                                )}
                              </td>

                              <td className="px-3 py-2 border border-black">
                                {qGen ? (
                                  <span className="inline-flex px-2 py-1 rounded-full border border-black text-[10px] font-extrabold bg-indigo-100 text-indigo-900">
                                    {L.generated} ({a.quotationNo || "-"})
                                  </span>
                                ) : (
                                  <span className="inline-flex px-2 py-1 rounded-full border border-black text-[10px] font-extrabold bg-slate-100 text-slate-700">
                                    {L.notGenerated}
                                  </span>
                                )}
                              </td>

                              <td className="px-3 py-2 border border-black">
                                {qGen ? (
                                  <a
                                    href={a.quotationPdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-extrabold"
                                  >
                                    📄 {L.viewDownload}
                                  </a>
                                ) : canDeleteReq(a) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteReq(a)}
                                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-extrabold"
                                  >
                                    🗑 {L.delete}
                                  </button>
                                ) : (
                                  <span className="inline-flex px-2 py-1 rounded-full border border-black text-[10px] font-extrabold bg-slate-100 text-slate-700">
                                    {L.locked}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-3 flex gap-2 flex-wrap">
                  <a
                    href={buildMachineryLink()}
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-extrabold"
                  >
                    🚜 {L.applyNew}
                  </a>
                  <button
                    type="button"
                    onClick={() => setActivePanel("dashboard")}
                    className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[12px] font-extrabold"
                  >
                    ← {L.back}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* DOCS */}
          {activePanel === "docs" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[12px] font-extrabold text-slate-900">{L.docsTitle}</div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {L.uploaded}: <b>{docsCount.uploaded}/{docsCount.total}</b>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={uploadDocuments}
                  disabled={uploadingDocs}
                  className={cx(
                    "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-extrabold text-white",
                    uploadingDocs ? "bg-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                  )}
                >
                  {uploadingDocs ? L.uploading : `✅ ${L.actionUploadSelected}`}
                </button>
              </div>

              <div className="p-4">
                {uploadMsg ? (
                  <div
                    className={cx(
                      "mb-4 rounded-xl border p-3 text-[11px] font-semibold",
                      uploadMsg.startsWith("✅")
                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                        : "bg-rose-50 border-rose-200 text-rose-900"
                    )}
                  >
                    {uploadMsg}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      Uploaded
                    </div>
                    <div className="text-[18px] font-extrabold text-slate-900 mt-1">
                      {docsCount.uploaded}/{docsCount.total}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      Tip
                    </div>
                    <div className="text-[11px] text-slate-700 mt-1">
                      Upload only what you have. You can update anytime.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                      Action
                    </div>
                    <div className="text-[11px] text-slate-700 mt-1">
                      Select files → click Upload
                    </div>
                  </div>
                </div>

                {/* Collapsible folders */}
                <div className="rounded-xl border border-black overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDocsOpen((v) => !v)}
                    className="w-full px-4 py-3 bg-slate-50 border-b border-black flex items-center justify-between text-left"
                  >
                    <div>
                      <div className="text-[12px] font-extrabold text-slate-900">{L.folders}</div>
                      <div className="text-[10px] text-slate-600">{L.foldersSub}</div>
                    </div>
                    <div className="text-[12px] font-extrabold text-slate-700">{docsOpen ? "▲" : "▼"}</div>
                  </button>

                  {docsOpen ? (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DocTile
                        icon="🖼️"
                        title={lang === "en" ? "Applicant Photo" : lang === "hi" ? "आवेदक फोटो" : "અરજદાર ફોટો"}
                        hint={lang === "en" ? "Image only" : lang === "hi" ? "केवल इमेज" : "માત્ર ઇમેજ"}
                        url={docsFilled.photo_url}
                        accept="image/*"
                        onPick={(f) => setFile("photo", f)}
                        langText={L}
                      />

                      <DocTile
                        icon="🪪"
                        title={lang === "en" ? "Aadhaar Card" : lang === "hi" ? "आधार कार्ड" : "આધાર કાર્ડ"}
                        hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                        url={docsFilled.aadhaar_url}
                        accept="image/*,application/pdf"
                        onPick={(f) => setFile("aadhaar", f)}
                        langText={L}
                      />

                      <DocTile
                        icon="🗳️"
                        title={lang === "en" ? "Election Card" : lang === "hi" ? "वोटर कार्ड" : "મતદાર કાર્ડ"}
                        hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                        url={docsFilled.election_url}
                        accept="image/*,application/pdf"
                        onPick={(f) => setFile("election", f)}
                        langText={L}
                      />

                      <DocTile
                        icon="📄"
                        title={lang === "en" ? "Land Record (7/12 / 8A)" : lang === "hi" ? "भूमि रिकॉर्ड (7/12 / 8A)" : "જમીન રેકોર્ડ (7/12 / 8A)"}
                        hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                        url={docsFilled.land_record_url}
                        accept="image/*,application/pdf"
                        onPick={(f) => setFile("land", f)}
                        langText={L}
                      />

                      <DocTile
                        icon="💡"
                        title={lang === "en" ? "Latest Light Bill" : lang === "hi" ? "ताज़ा लाइट बिल" : "તાજેતરનું લાઇટ બિલ"}
                        hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                        url={docsFilled.light_bill_url}
                        accept="image/*,application/pdf"
                        onPick={(f) => setFile("light", f)}
                        langText={L}
                      />

                      {/* Bank Proof */}
                      <div className="rounded-2xl border border-black bg-white shadow-sm overflow-hidden md:col-span-2">
                        <div className="px-4 py-3 bg-slate-50 border-b border-black flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-[18px]">
                              🏦
                            </div>
                            <div>
                              <div className="text-[12px] font-extrabold text-slate-900">
                                {lang === "en" ? "Bank Proof" : lang === "hi" ? "बैंक प्रूफ" : "બેંક પ્રૂફ"}
                              </div>
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                {lang === "en" ? "Upload any one or both" : lang === "hi" ? "कोई एक या दोनों अपलोड करें" : "એક અથવા બન્ને અપલોડ કરો"}
                              </div>
                            </div>
                          </div>
                          <span className="text-[10px] font-extrabold px-2 py-1 rounded-full border border-black bg-slate-100 text-slate-700">
                            {docsFilled.bank_cheque_url || docsFilled.bank_passbook_url ? `${L.uploadedOk} ✅` : `${L.pending} ⏳`}
                          </span>
                        </div>

                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <DocTile
                            icon="🏦"
                            title={lang === "en" ? "Cancelled Cheque" : lang === "hi" ? "कैंसल चेक" : "કૅન્સલ ચેક"}
                            hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                            url={docsFilled.bank_cheque_url}
                            accept="image/*,application/pdf"
                            onPick={(f) => setFile("bank_cheque", f)}
                            langText={L}
                          />
                          <DocTile
                            icon="📒"
                            title={lang === "en" ? "Passbook" : lang === "hi" ? "पासबुक" : "પાસબુક"}
                            hint={lang === "en" ? "Image or PDF" : lang === "hi" ? "इमेज या PDF" : "ઇમેજ અથવા PDF"}
                            url={docsFilled.bank_passbook_url}
                            accept="image/*,application/pdf"
                            onPick={(f) => setFile("bank_passbook", f)}
                            langText={L}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* BENEFITS */}
          {activePanel === "benefits" && (
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[12px] font-extrabold text-slate-900">{L.benefitsTitle}</div>
                  <div className="text-[11px] text-slate-600 mt-1">
                    {componentsLoading ? "Loading…" : L.benefitsSub}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAllBenefits((v) => !v)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-extrabold border bg-white hover:bg-slate-50 border-slate-200 text-slate-800"
                >
                  {showAllBenefits ? L.showLess : L.showAll}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {benefitsToShow.map((card) => {
                  const isLive = !!card.live && !!card.link;
                  const hasLink = !!card.link;

                  let href = undefined;
                  if (isLive && hasLink) {
                    href = card.id === "MACH_BANK" ? buildMachineryLink() : card.link;
                  }

                  return (
                    <div
                      key={card.id}
                      className="rounded-2xl border border-black bg-white p-4 shadow-sm hover:shadow-md transition"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-[16px]">{card.icon}</span>
                          <div className="text-[12px] font-extrabold text-slate-900">{card.title}</div>
                        </div>
                        <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-extrabold">
                          {L.enabled}
                        </span>
                      </div>

                      <div className="mt-2 text-[11px] text-slate-600 leading-relaxed">
                        {card.description}
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        <span className="text-[10px] px-2 py-1 rounded-full bg-slate-200 text-slate-700 border border-black">
                          {isLive ? L.live : L.soon}
                        </span>

                        {hasLink ? (
                          <a
                            href={href}
                            onClick={(e) => {
                              if (!isLive) e.preventDefault();
                            }}
                            className={cx(
                              "px-3 py-1 rounded-full text-[10px] font-extrabold border border-black",
                              isLive
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-slate-300 text-slate-600 cursor-not-allowed"
                            )}
                          >
                            {card.ctaLabel}
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="px-3 py-1 rounded-full bg-slate-300 text-[10px] text-slate-600 cursor-not-allowed border border-black"
                          >
                            {L.applySoon}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
